<template>
  <b-container fluid>
    <b-row>
      <editable-map
        editable
        ref="map"
        id="map"
        :zoom="12"
        :center="center"
        style="width: 100%; height: 500px"
        @ready="emitReady"
        :bounds.sync="bounds"
        v-if="bounds"
      >
        <l-tile-layer
          v-for="tileProvider in tileProviders"
          :key="tileProvider.name"
          :name="tileProvider.name"
          :visible="tileProvider.visible"
          :url="tileProvider.url"
          :attribution="tileProvider.attribution"
          layer-type="base"
          :options="tileProvider.options"
          v-if="tileProvidersReady"
        />
        <l-control-layers
          position="topright"
          :collapsed="false"
          :sort-layers="false"
        />
        <l-control position="topleft">
          <div v-on:mouseover="disableMapInteraction" v-on:mouseout="enableMapInteraction">
            <b-form-group style="
              background: #fff;
              padding: 10px;
              height: auto;
              max-height: 320px;
              overflow-y: scroll;
              z-index: 1000;
              max-width: 250px;
            ">
              <b-form-checkbox
                v-model="allSelected"
                :indeterminate="indeterminate"
                @change="toggleAllAreas"
              >
                {{ allSelected ? $gettext('Un-select All') : $gettext('Select All') }}
              </b-form-checkbox>

              <b-form-checkbox-group class="ml-4" stacked v-model="selectedAreas" :options="allAreasList">
              </b-form-checkbox-group>
            </b-form-group>
          </div>
        </l-control>
        <l-control position="bottomleft">
          <div style="
            z-index: 100;
            background: #fff;
            padding: 10px;
            max-width: 250px;
          ">
          <b-form-checkbox v-model="showTooltips">
            <translate>Show names</translate>
          </b-form-checkbox>
          </div>
        </l-control>
        <l-control-fullscreen position="bottomleft"
          :options="{title: { false: $gettext('Go fullscreen'), true: $gettext('Back to normal') }}"
        />
        <l-control position="topright" >
          <b-button size="sm" @click="fitMap" v-translate>
            Fit map
          </b-button>
        </l-control>
        <editable-polygon
          ref="otherPolygon"
          v-if="visibleOtherAreas.length > 0"
          :editable="false"
          :lat-lngs="item.coords"
          v-for="(item, index) in visibleOtherAreas"
          :key="index"
        >
          <l-tooltip v-if="showTooltips" :content="item.name" :options="{className: 'leaflet-tooltip-areas', permanent: true, direction: 'center'}" />
        </editable-polygon>
      </editable-map>
    </b-row>
  </b-container>
</template>

<script>
import { latLngBounds } from 'leaflet'
import {
  LTileLayer,
  LControlLayers,
  LControl,
  LTooltip
} from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import { EditableMap, EditablePolygon } from 'vue2-leaflet-editable'
import LControlFullscreen from 'vue2-leaflet-fullscreen'
import { getCountry } from '@/services/country'

export default {
  components: {
    LTileLayer,
    LControlLayers,
    LControl,
    LTooltip,
    EditableMap,
    EditablePolygon,
    LControlFullscreen
  },
  props: {
    type: {
      type: String,
      default: 'areas'
    },
    countrizeField: {
      type: String,
      default: 'country'
    }
  },
  data() {
    return {
      allAreas: [],
      selectedAreas: [],
      allAreasList: [],
      visibleOtherAreas: [],
      map: null,
      mapReady: false,
      center: [-34.6158036, -58.5035093],
      bounds: null,
      showTooltips: false,
      country: null,
      allSelected: false,
      indeterminate: false
    }
  },
  watch: {
    selectedAreas(val) {
      this.visibleOtherAreas = []
      if (val.length === 0) {
        this.indeterminate = false
        this.allSelected = false
      } else if (val.length === this.allAreasList.length) {
        this.indeterminate = false
        this.allSelected = true
      } else {
        this.indeterminate = true
        this.allSelected = false
      }
      this.$nextTick(() => {
        this.visibleOtherAreas = val.map(area => {
          return {
            name: this.allAreas.find(x => x.name === area).name,
            coords: JSON.parse(JSON.stringify(this.allAreas.find(x => x.name === area).area)).coordinates.map(linearRing => linearRing.slice(0, linearRing.length - 1)
              .map(([lng, lat]) => ({lat, lng}))
            )[0]
          }
        }).filter(Boolean)
        this.fitMap()
      })
    },
    showTooltips(val) {
      // HACK: needed to reset tooltips from scratch avoiding misplacing
      const selectedAreas = this.selectedAreas
      this.selectedAreas = []
      this.selectedAreas = selectedAreas
    }
  },
  created() {
    this.checkMapProviders()
    this.fetchPlatforms()
    this.fetchAreas()
  },
  mounted() {
  },
  methods: {
    toggleAllAreas(checked) {
      this.selectedAreas = checked ? this.allAreasList.slice() : []
    },
    disableMapInteraction() {
      this.map.dragging.disable()
      this.map.touchZoom.disable()
      this.map.doubleClickZoom.disable()
      this.map.scrollWheelZoom.disable()
      this.map.boxZoom.disable()
      this.map.keyboard.disable()
      if (this.map.tap) this.map.tap.disable()
    },
    enableMapInteraction() {
      this.map.dragging.enable()
      this.map.touchZoom.enable()
      this.map.doubleClickZoom.enable()
      this.map.scrollWheelZoom.enable()
      this.map.boxZoom.enable()
      this.map.keyboard.enable()
      if (this.map.tap) this.map.tap.enable()
    },
    fetchAreas(page = 0) {
      this.$nextTick(() => {
        const params = {
          page,
          sort: [
            {
              key: 'name',
              direction: 'asc'
            }
          ]
        }

        if (getCountry() !== null || this.country) {
          params.filter_groups = [{
            filters: [
              [this.countrizeField, 'eq', (getCountry() || this.country)]
            ]
          }]
        }

        this.$http.get(this.$appConfig.api.baseUrl + this.type, {params})
          .then(response => {
            if (response.body.data.length > 0) {
              this.allAreas = this.allAreas.concat(response.body.data)
              this.allAreasList = this.allAreasList.concat(response.body.data.map(area => area.name).filter(Boolean))
              this.selectedAreas = this.selectedAreas.concat(response.body.data.map(area => area.name).filter(Boolean))
              this.fetchAreas(response.body.pagination.page + 1)
            }
          })
      })
    },
    fitMap() {
      const bounds = latLngBounds(this.visibleOtherAreas.map(o => o.coords))
      this.bounds = bounds
      this.fitted = true
    },
    emitReady() {
      setTimeout(function () {
        this.map = this.$refs.map.mapObject
        this.map.invalidateSize()
        this.map.on('popupclose', () => this.emitCourier(null))
        this.mapReady = true
        this.$emit('ready', true)
      }.bind(this), 500)
    }
  }
}
</script>

<style lang="scss">
.leaflet-tooltip-areas {
  padding: 0 3px !important;
}
</style>
